export const PARTNER_AUTH_PREFIXES = [
  '/partner/login',
  '/partner/signup',
  '/partner/setup-password',
];

export const PARTNER_APP_HOME = '/partner/dashboard';

export const PARTNER_TRIPS_PATH = '/partner/trips';
export const PARTNER_TRIPS_VIEW_STORAGE_KEY = 'partnerTripsViewMode';

export const partnerTripsPath = (viewMode = 'list') => (
  viewMode === 'board' ? `${PARTNER_TRIPS_PATH}?view=board` : PARTNER_TRIPS_PATH
);

export const readStoredPartnerTripsView = () => {
  try {
    return sessionStorage.getItem(PARTNER_TRIPS_VIEW_STORAGE_KEY) === 'board' ? 'board' : 'list';
  } catch {
    return 'list';
  }
};

export const persistPartnerTripsView = (viewMode) => {
  try {
    sessionStorage.setItem(
      PARTNER_TRIPS_VIEW_STORAGE_KEY,
      viewMode === 'board' ? 'board' : 'list',
    );
  } catch {
    // Private mode or disabled storage should not break navigation.
  }
};

export const resolvePartnerTripsView = (search = '') => {
  const view = new URLSearchParams(String(search || '').replace(/^\?/, '')).get('view');
  if (view === 'board' || view === 'list') return view;
  return readStoredPartnerTripsView();
};

export const partnerTripsReturnPath = () => partnerTripsPath(readStoredPartnerTripsView());

export const hasPartnerSession = () => Boolean(
  typeof window !== 'undefined' && localStorage.getItem('partnerToken')
);

/** After login, never dump an agent back on the public marketing or auth pages. */
export const partnerPostLoginPath = (fromPathname = '') => {
  const path = String(fromPathname || '').replace(/\/+$/, '') || '/';
  if (path === '/' || path === '/partner') return PARTNER_APP_HOME;
  if (PARTNER_AUTH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
    return PARTNER_APP_HOME;
  }
  return fromPathname || PARTNER_APP_HOME;
};

/** Top header bar only (logo row). Bottom nav is separate on xs. */
export const PARTNER_CHROME_HEIGHT = { xs: 60, md: 56 };

/** Fixed partner desktop left rail (md+). */
export const PARTNER_SIDEBAR_WIDTH = 240;

/** Fixed partner mobile bottom tab bar (excludes safe-area inset). */
export const PARTNER_BOTTOM_NAV_HEIGHT = 56;

export const partnerWorkspaceViewportSx = {
  height: {
    xs: `calc(100dvh - ${PARTNER_CHROME_HEIGHT.xs}px - ${PARTNER_BOTTOM_NAV_HEIGHT}px)`,
    md: `calc(100dvh - ${PARTNER_CHROME_HEIGHT.md}px)`,
  },
  maxHeight: {
    xs: `calc(100dvh - ${PARTNER_CHROME_HEIGHT.xs}px - ${PARTNER_BOTTOM_NAV_HEIGHT}px)`,
    md: `calc(100dvh - ${PARTNER_CHROME_HEIGHT.md}px)`,
  },
  overflow: 'hidden',
};

/** Extra bottom padding so content clears the mobile bottom nav + safe area. */
export const partnerMobileBottomPadSx = {
  pb: {
    xs: `calc(${PARTNER_BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px) + 16px)`,
    md: 0,
  },
};

/** Offset page content for the desktop left rail. */
export const partnerDesktopSidebarPadSx = {
  pl: { md: `${PARTNER_SIDEBAR_WIDTH}px` },
};

export const isPartnerWorkspacePath = (pathname = '') => {
  if (!pathname.startsWith('/partner/')) return false;
  return !PARTNER_AUTH_PREFIXES.some((prefix) => (
    pathname === prefix || pathname.startsWith(`${prefix}/`)
  ));
};

export const shouldUsePartnerChrome = (pathname = '', partner = null) => (
  Boolean(partner)
  && (isPartnerWorkspacePath(pathname) || pathname.startsWith('/trips/create'))
);

export const getPartnerNavKey = (pathname = '') => {
  if (pathname.startsWith('/partner/trips') || pathname.startsWith('/partner/master-trips')) return 'trips';
  if (pathname.startsWith('/partner/packages/dmcs')) return 'dmcs';
  if (pathname.startsWith('/partner/packages')) return 'packages';
  if (pathname.startsWith('/partner/inventory')) return 'inventory';
  if (pathname.startsWith('/partner/add-ons')) return 'addons';
  if (pathname.startsWith('/partner/analytics')) return 'analytics';
  if (pathname.startsWith('/partner/bookings')) return 'bookings';
  if (pathname.startsWith('/partner/requests')) return 'requests';
  if (pathname.startsWith('/partner/inquiries')) return 'inquiries';
  if (pathname.startsWith('/partner/private-referrals')) return 'privateReferrals';
  if (pathname.startsWith('/partner/team')) return 'team';
  if (pathname.startsWith('/partner/profile') || pathname.startsWith('/partner/billing')) return 'settings';
  if (pathname.startsWith('/partner/how-to')) return 'howTo';
  if (pathname.startsWith('/partner/clients')) return 'clients';
  if (pathname.startsWith('/partner/dashboard')) return 'dashboard';
  return null;
};

/** Desktop left-rail primary destinations. */
export const PARTNER_DESKTOP_RAIL_PRIMARY = [
  { key: 'dashboard', to: '/partner/dashboard' },
  { key: 'trips', to: '/partner/trips' },
  { key: 'clients', to: '/partner/clients' },
  { key: 'packages', to: '/partner/packages' },
  { key: 'inventory', to: '/partner/inventory' },
  { key: 'addons', to: '/partner/add-ons' },
  { key: 'requests', to: '/partner/requests' },
  { key: 'inquiries', to: '/partner/inquiries' },
  { key: 'privateReferrals', to: '/partner/private-referrals' },
  { key: 'bookings', to: '/partner/bookings' },
  { key: 'analytics', to: '/partner/analytics' },
  { key: 'team', to: '/partner/team' },
];

/** Desktop left-rail secondary / settings. */
export const PARTNER_DESKTOP_RAIL_SECONDARY = [
  { key: 'howTo', to: '/partner/how-to' },
  { key: 'profile', to: '/partner/profile' },
  { key: 'billing', to: '/partner/billing' },
];

export const PARTNER_MOBILE_BOTTOM_TABS = [
  { key: 'dashboard', to: '/partner/dashboard', labelKey: 'partnerNav.dashboard' },
  { key: 'trips', to: '/partner/trips', labelKey: 'partnerNav.trips' },
  { key: 'packages', to: '/partner/packages', labelKey: 'partnerNav.packages' },
  { key: 'requests', to: '/partner/requests', labelKey: 'partnerNav.requests' },
  { key: 'bookings', to: '/partner/bookings', labelKey: 'partnerNav.bookings' },
];
